import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Trash2, Send, Loader2 } from 'lucide-react';

/** WhatsApp-style voice preview: listen / pause / re-listen before sending. */
export default function VoicePreviewBar({ url, onSend, onCancel, sending, className = '' }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [dur, setDur] = useState(0);
  const [cur, setCur] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onMeta = () => {
      if (a.duration === Infinity || isNaN(a.duration)) {
        a.currentTime = 1e7;
        const fix = () => {
          a.removeEventListener('timeupdate', fix);
          a.currentTime = 0;
          setDur(a.duration === Infinity ? 0 : a.duration);
        };
        a.addEventListener('timeupdate', fix);
      } else {
        setDur(a.duration);
      }
    };
    const onTime = () => setCur(a.currentTime);
    const onEnd = () => { setPlaying(false); setCur(0); };
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('ended', onEnd);
    return () => {
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('ended', onEnd);
    };
  }, [url]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().catch(() => {}); setPlaying(true); }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * dur;
    setCur(a.currentTime);
  };

  const fmt = (s) => {
    const n = Math.max(0, Math.floor(s || 0));
    return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
  };

  const pct = dur > 0 ? Math.min(100, (cur / dur) * 100) : 0;

  return (
    <div
      className={`bg-gray-900/95 border border-fuchsia-500/40 rounded-2xl px-3 py-2.5 flex items-center gap-2.5 shadow-xl ${className}`}
      data-testid="voice-preview-bar"
    >
      <audio ref={audioRef} src={url} preload="metadata" />
      <button
        type="button"
        onClick={onCancel}
        disabled={sending}
        className="p-2 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-300 shrink-0"
        data-testid="voice-preview-cancel"
        aria-label="Supprimer le vocal"
      >
        <Trash2 className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={toggle}
        className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white shrink-0"
        data-testid="voice-preview-play"
        aria-label={playing ? 'Pause' : 'Écouter'}
      >
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <div className="flex-1 min-w-0 cursor-pointer py-2" onClick={seek} data-testid="voice-preview-progress">
        <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-fuchsia-500 to-purple-500 transition-[width]" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="text-[11px] font-mono text-gray-300 shrink-0">{fmt(cur)}{dur ? ` / ${fmt(dur)}` : ''}</span>
      <button
        type="button"
        onClick={onSend}
        disabled={sending}
        className="p-2.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:brightness-110 text-white shrink-0 disabled:opacity-50"
        data-testid="voice-preview-send"
        aria-label="Envoyer le vocal"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
      </button>
    </div>
  );
}
